import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Calendar, Clock, DollarSign, User, Check, X } from 'lucide-react';

export default function HealingSessions() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [notes, setNotes] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  useEffect(() => {
    loadSessions();
    loadBookings();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/healing-sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const handleBookSession = (session) => {
    setSelectedSession(session);
    setShowBookingForm(true);
    setBookingSuccess(false);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;

    try {
      await axios.post(
        `${API}/bookings`,
        {
          session_id: selectedSession.id,
          booking_date: selectedSlot,
          notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookingSuccess(true);
      setSelectedSlot('');
      setNotes('');
      loadBookings();
      setTimeout(() => {
        setShowBookingForm(false);
        setBookingSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to book session:', error);
    }
  };

  return (
    <div className="space-y-8" data-testid="healing-sessions-page">
      {/* Header */}
      <div className="glass-card p-6">
        <h1 className="text-3xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
          Healing Sessions
        </h1>
        <p style={{ color: currentColors.textMuted }}>
          Book personalized healing sessions with expert practitioners
        </p>
      </div>

      {/* My Bookings */}
      {bookings.length > 0 && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-cinzel" style={{ color: currentColors.primary }}>My Bookings</h2>
            <Link
              to="/my-bookings"
              className="text-sm font-medium hover:opacity-80 transition-all"
              style={{ color: currentColors.accent }}
            >
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {bookings.slice(0, 3).map((booking) => (
              <div
                key={booking.id}
                className="flex items-center justify-between p-4 rounded-lg"
                style={{ 
                  backgroundColor: `${currentColors.secondary}10`,
                  border: `1px solid ${currentColors.primary}20`
                }}
                data-testid={`booking-${booking.id}`}
              >
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5" style={{ color: currentColors.secondary }} />
                  <div>
                    <p className="font-medium" style={{ color: currentColors.text }}>
                      Session ID: {booking.session_id.slice(0, 8)}...
                    </p>
                    <p className="text-sm" style={{ color: currentColors.textMuted }}>
                      {new Date(booking.booking_date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <span 
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: booking.status === 'confirmed' ? 'rgba(34, 197, 94, 0.2)' :
                                     booking.status === 'pending' ? 'rgba(234, 179, 8, 0.2)' :
                                     'rgba(239, 68, 68, 0.2)',
                    color: booking.status === 'confirmed' ? '#22c55e' :
                           booking.status === 'pending' ? '#eab308' :
                           '#ef4444'
                  }}
                >
                  {booking.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Sessions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sessions.map((session) => (
          <div
            key={session.id}
            className="glass-card overflow-hidden hover:scale-[1.02] transition-all"
            data-testid={`session-card-${session.id}`}
          >
            <div 
              className="h-48 flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${currentColors.secondary}40, ${currentColors.primary}40)`
              }}
            >
              {session.image_url ? (
                <img src={session.image_url} alt={session.title} className="w-full h-full object-cover" />
              ) : (
                <div className="text-6xl">🌿</div>
              )}
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span 
                  className="px-3 py-1 text-xs font-medium rounded-full capitalize"
                  style={{ 
                    backgroundColor: `${currentColors.accent}20`,
                    color: currentColors.accent
                  }}
                >
                  {session.session_type}
                </span>
                <div className="flex items-center text-lg font-bold" style={{ color: currentColors.primary }}>
                  <DollarSign className="w-5 h-5" />
                  {session.price}
                </div>
              </div>

              <h3 className="text-xl font-cinzel mb-2" style={{ color: currentColors.text }}>
                {session.title}
              </h3>
              <p className="text-sm mb-4 line-clamp-2" style={{ color: currentColors.textMuted }}>
                {session.description}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm" style={{ color: currentColors.textMuted }}>
                  <User className="w-4 h-4 mr-2" />
                  {session.instructor}
                </div>
                <div className="flex items-center text-sm" style={{ color: currentColors.textMuted }}>
                  <Clock className="w-4 h-4 mr-2" />
                  {session.duration} minutes
                </div>
                <div className="flex items-center text-sm" style={{ color: currentColors.textMuted }}>
                  <Calendar className="w-4 h-4 mr-2" />
                  {session.available_slots.length} slots available
                </div>
              </div>

              <button
                onClick={() => handleBookSession(session)}
                className="w-full py-3 rounded-lg font-medium transition-all hover:opacity-90"
                style={{ 
                  background: `linear-gradient(135deg, ${currentColors.primary}, ${currentColors.secondary})`,
                  color: currentColors.background
                }}
                data-testid={`book-button-${session.id}`}
              >
                Book Session
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Booking Modal */}
      {showBookingForm && selectedSession && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div 
            className="rounded-2xl max-w-md w-full p-8 relative"
            style={{ 
              backgroundColor: currentColors.background,
              border: `1px solid ${currentColors.primary}30`
            }}
            data-testid="booking-modal"
          >
            {/* Close button */}
            <button
              onClick={() => {
                setShowBookingForm(false);
                setSelectedSlot('');
                setNotes('');
              }}
              className="absolute top-4 right-4 p-1 rounded-full hover:opacity-80"
              style={{ color: currentColors.textMuted }}
            >
              <X className="w-5 h-5" />
            </button>

            {bookingSuccess ? (
              <div className="text-center">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}
                >
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
                  Booking Confirmed!
                </h2>
                <p style={{ color: currentColors.textMuted }}>
                  We'll send you a confirmation email shortly.
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-cinzel mb-4" style={{ color: currentColors.primary }}>
                  Book {selectedSession.title}
                </h2>
                <form onSubmit={handleSubmitBooking} className="space-y-4">
                  <div>
                    <label 
                      className="block text-sm font-medium mb-2"
                      style={{ color: currentColors.text }}
                    >
                      Select Time Slot
                    </label>
                    <select
                      value={selectedSlot}
                      onChange={(e) => setSelectedSlot(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: `${currentColors.primary}10`,
                        border: `1px solid ${currentColors.primary}30`,
                        color: currentColors.text
                      }}
                      required
                      data-testid="slot-select"
                    >
                      <option value="" style={{ backgroundColor: currentColors.background, color: currentColors.text }}>
                        Choose a time...
                      </option>
                      {selectedSession.available_slots.map((slot, index) => (
                        <option 
                          key={index} 
                          value={slot}
                          style={{ backgroundColor: currentColors.background, color: currentColors.text }}
                        >
                          {new Date(slot).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-2"
                      style={{ color: currentColors.text }}
                    >
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: `${currentColors.primary}10`,
                        border: `1px solid ${currentColors.primary}30`,
                        color: currentColors.text
                      }}
                      rows="3"
                      placeholder="Any specific concerns or questions?"
                      data-testid="notes-input"
                    />
                  </div>

                  <div 
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: `${currentColors.secondary}15` }}
                  >
                    <div className="flex justify-between items-center">
                      <span style={{ color: currentColors.text }}>Total Amount:</span>
                      <span className="text-2xl font-bold" style={{ color: currentColors.primary }}>
                        ${selectedSession.price}
                      </span>
                    </div>
                    <p className="text-sm mt-2" style={{ color: currentColors.textMuted }}>
                      Payment will be processed after confirmation
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="flex-1 py-3 rounded-lg font-medium transition-all hover:opacity-90"
                      style={{ 
                        background: `linear-gradient(135deg, ${currentColors.primary}, ${currentColors.secondary})`,
                        color: currentColors.background
                      }}
                      data-testid="confirm-booking-button"
                    >
                      Confirm Booking
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBookingForm(false);
                        setSelectedSlot('');
                        setNotes('');
                      }}
                      className="px-6 py-3 rounded-lg font-medium transition-all hover:opacity-80"
                      style={{ 
                        backgroundColor: `${currentColors.textMuted}20`,
                        color: currentColors.text
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
